import { camelCase, fromPairs } from 'lodash';
import { useMemo } from 'react';
import { UserStatus, UserType } from 'api/models';
import { FieldOption } from 'api/tasks';
import {
  FieldType,
  FilterableField,
  SourceType,
  filterableFieldType,
} from 'components/organisms/tasks/fields';
import { useCustomerTeamMemberFieldsQuery } from 'queries';
import { useUsersQuery } from 'queries/members';
import { userOption } from 'utils/project-fields/construct-user-option';

export const useCustomerTeamMemberFieldsAsFilterableFields = () => {
  const { data: teamMemberFields, isLoading } =
    useCustomerTeamMemberFieldsQuery();

  const { data: users, isLoading: isUserLoading } = useUsersQuery();
  const userMeta = useMemo(() => {
    const nativeUsers = (users ?? []).filter(
      (u) =>
        u.userType === UserType.Native && u.userStatus !== UserStatus.Inactive,
    );

    return {
      options: nativeUsers.map(userOption) as FieldOption<number>[],
      users: {
        by_user_id: fromPairs(nativeUsers.map((x) => [x.userId, x])),
        active_user_ids: nativeUsers.map((x) => x.userId),
        non_external_user_ids: nativeUsers.map((x) => x.userId),
        by_project_id: {},
      },
    };
  }, [users]);

  return useMemo(
    () => ({
      loading: isLoading || isUserLoading,
      fields: (
        (teamMemberFields ?? [])
          .map((field) => ({
            ...field,
            fieldName: camelCase(field.fieldName),
            type: filterableFieldType(field),
            sourceType: SourceType.Company,
          }))
          .map((field) =>
            [FieldType.SingleUser, FieldType.MultiUser].includes(field.type)
              ? { ...field, metaData: userMeta }
              : field,
          ) as FilterableField[]
      ).filter((field) => field.isEnabled),
    }),
    [isLoading, isUserLoading, teamMemberFields, userMeta],
  );
};
